import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AdminSchemaBasedEditor from "./AdminSchemaBasedEditor";

// Help content component for Sections
const SectionsHelpContent = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Overview</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        Sections define how data is displayed and edited in the document editor. 
        Each section maps to a UI component type and binds to data fields.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Section Types</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Text Editor</strong> - Rich text editor (most common)</p>
        <p><strong>Table Editor</strong> - Table with rows (medications, allergies)</p>
        <p><strong>Start/End Time</strong> - Time picker for session timing</p>
        <p><strong>Confirmation</strong> - Checkbox confirmation section</p>
        <p><strong>Therapy Timing</strong> - Specialized therapy timing UI</p>
        <p><strong>Treatment Information</strong> - Treatment summary display</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Display Options</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Expanded by Default</strong> - Section opens expanded when document loads</p>
        <p><strong>Show Header</strong> - Display the section title bar</p>
        <p><strong>Show in Sidebar</strong> - Include in navigation sidebar</p>
        <p><strong>Display History</strong> - Show previous values from past documents</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Export Settings</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Include in Exports</strong> - Which export types include this section (Doctor, Patient)</p>
        <p><strong>Export Behavior</strong> - How the section appears in exports (current only, hide if empty, etc.)</p>
        <p><strong>Default Export Text</strong> - Text shown if section is empty</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Table Configuration</h4>
      <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)' }}>
        For Table Editor sections, configure:
      </p>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Primary Field</strong> - Main data field for current items</p>
        <p><strong>Secondary Field</strong> - Field for past/historical items</p>
        <p><strong>Table Columns</strong> - Field definition for column structure</p>
        <p><strong>Sort Order</strong> - How rows are sorted</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Text Editor Options</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Toolbar Type</strong> - Full toolbar or none</p>
        <p><strong>Copy to Clipboard</strong> - Show copy button</p>
        <p><strong>Default Content</strong> - HTML template for new documents</p>
      </Box>
    </Box>

    <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', borderTop: '1px solid var(--color-border)', pt: 2 }}>
      Full documentation: <code>docs/admin-document-builder.md</code>
    </Box>
  </Box>
);

// Configuration for the Sections editor
const sectionsConfig = {
  collectionName: "documentSections",
  schemaKey: "sectionSchema",
  entityName: "Section",
  entityNamePlural: "Sections",
  searchFields: ['title', 'name'],
  idPlaceholder: "chiefComplaintSection",
  columns: [
    {
      key: "displayName",
      label: "Name",
      render: (item) => (
        <span className="table-primary-text" style={{ color: 'var(--color-primary)' }}>{item.displayName || item.title || item.id}</span>
      )
    },
    {
      key: "type",
      label: "Type",
      render: (item) => {
        const type = item.type || "unknown";
        const colorMap = {
          "EditableTextSection": "primary",
          "EditableTableSection": "success",
          "StartEndTime": "warning",
          "Confirmation": "secondary",
          "TherapyTiming": "warning",
          "TreatmentInformation": "error"
        };
        const labelMap = {
          "EditableTextSection": "Text",
          "EditableTableSection": "Table",
          "StartEndTime": "Time",
          "Confirmation": "Confirm",
          "TherapyTiming": "Therapy",
          "TreatmentInformation": "Treatment"
        };
        return (
          <Chip 
            label={labelMap[type] || type}
            size="small"
            color={colorMap[type] || "default"}
            variant="outlined"
          />
        );
      }
    },
    {
      key: "dataField",
      label: "Data Field",
      render: (item) => (
        <span style={{ fontFamily: 'monospace', fontSize: 'var(--text-sm)' }}>
          {item.dataField || "-"}
        </span>
      )
    },
    {
      key: "status",
      label: "Status",
      sortValue: (item) => {
        const version = item.version || 1;
        const publishedVersion = item.publishedVersion;
        return (publishedVersion && publishedVersion === version) ? "published" : "draft";
      },
      render: (item) => {
        const version = item.version || 1;
        const publishedVersion = item.publishedVersion;
        const isPublished = publishedVersion && publishedVersion === version;
        return (
          <Tooltip title={isPublished ? `Published v${publishedVersion}` : `Draft v${version}`} arrow>
            <Chip 
              label={isPublished ? "Published" : "Draft"}
              size="small"
              color={isPublished ? "success" : "warning"}
              variant="outlined"
            />
          </Tooltip>
        );
      }
    }
  ],
  HelpContent: SectionsHelpContent
};

const AdminSectionsEditor = () => {
  return <AdminSchemaBasedEditor config={sectionsConfig} />;
};

export default AdminSectionsEditor;
